import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  LockKeyhole,
  Mail,
  MessageCircleMore,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../api/client';
import { ThemeToggle } from '../components/ThemeToggle';
import { ProjectLockup } from '../components/ProjectLockup';
import { INBOX_PATH } from '../lib/routeConfig';
import { WorkspaceLoading } from '../components/WorkspaceLoading';
import { SessionUnavailable } from '../components/SessionUnavailable';

const PRODUCT_POINTS = [
  { icon: MessageCircleMore, key: 'login.feature_inbox' },
  { icon: UsersRound, key: 'login.feature_routing' },
  { icon: BarChart3, key: 'login.feature_analytics' },
] as const;

export default function Login() {
  const { login, isAuthenticated, loading: authLoading, sessionUnavailable, retrySession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const previous = (location.state as {
    from?: { pathname?: string; search?: string; hash?: string };
  })?.from;
  const from = previous?.pathname
    ? `${previous.pathname}${previous.search ?? ''}${previous.hash ?? ''}`
    : INBOX_PATH;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);   // unchecked by default: the existing session policy
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (authLoading) return <WorkspaceLoading />;

  // The Android app opens on /login. If the session could not be checked (offline, 429, 5xx), a valid remembered
  // session must not look signed out: show the retry screen; a successful re-check redirects in below.
  if (sessionUnavailable && !isAuthenticated) return <SessionUnavailable onRetry={retrySession} />;

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password, remember);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError(t('login.error_locked'));
        } else if (err.status === 401) {
          setError(t('login.error_credentials'));
        } else {
          setError(t('login.error_generic'));
        }
      } else {
        setError(t('login.error_connection'));
      }
    } finally {
      setLoading(false);
    }
  }

  function toggleLanguage() {
    i18n.changeLanguage(i18n.language === 'ar' ? 'en' : 'ar');
  }

  return (
    <div className="min-h-dvh bg-canvas lg:grid lg:h-dvh lg:grid-cols-[minmax(0,1.08fr)_minmax(430px,0.92fr)] lg:overflow-hidden">
      <div className="fixed end-4 top-4 z-20 flex items-center gap-2">
        <ThemeToggle showLabel={false} />
        <button
          onClick={toggleLanguage}
          data-testid="lang-toggle"
          className="touch-target h-9 rounded-full border border-default bg-surface px-3 text-xs font-semibold
            text-secondary shadow-sm transition-colors duration-base ease-standard
            hover:bg-surface-hover hover:text-primary focus-visible:outline-2 focus-visible:outline-primary"
        >
          {t('lang_toggle')}
        </button>
      </div>

      <aside className="relative hidden h-dvh min-h-0 overflow-hidden border-e border-default/70 bg-pane px-10 py-7 lg:flex lg:flex-col xl:px-14 xl:py-8">
        <div className="absolute -start-24 top-1/4 size-72 rounded-full bg-primary/[0.06] blur-3xl" aria-hidden="true" />
        <div className="relative">
          <ProjectLockup />
        </div>

        <div className="relative my-auto max-w-[660px] py-5">
          <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold text-primary">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {t('login.eyebrow')}
          </p>
          <h1 className="max-w-[15ch] text-[clamp(2.5rem,3.7vw,3.85rem)] font-bold leading-[1.02] tracking-[-0.04em] text-primary">
            {t('login.hero_title')}
          </h1>
          <p className="mt-4 max-w-[58ch] text-base leading-6 text-secondary">
            {t('login.hero_description')}
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {PRODUCT_POINTS.map(({ icon: Icon, key }) => (
              <div key={key} className="rounded-2xl border border-default/70 bg-surface/80 p-3 shadow-sm">
                <span className="flex size-8 items-center justify-center rounded-lg bg-primary/[0.10] text-primary">
                  <Icon size={17} strokeWidth={1.8} aria-hidden="true" />
                </span>
                <p className="mt-2 text-[13px] font-semibold leading-[1.35rem] text-primary">{t(key)}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 overflow-hidden rounded-[1.4rem] border border-default/70 bg-surface shadow-lg">
            <div className="flex items-center justify-between border-b border-default/60 px-4 py-3">
              <span>
                <span className="block text-sm font-semibold text-primary">{t('login.preview_title')}</span>
                <span className="mt-0.5 block text-xs text-muted">{t('login.preview_subtitle')}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/[0.10] px-2.5 py-1 text-xs font-semibold text-primary">
                <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
                {t('login.preview_live')}
              </span>
            </div>
            <div className="grid grid-cols-[0.9fr_1.1fr]">
              <div className="border-e border-default/60 px-4 py-2">
                {[
                  ['AM', t('login.preview_customer_one'), t('login.preview_message_one')],
                  ['NS', t('login.preview_customer_two'), t('login.preview_message_two')],
                  ['YA', t('login.preview_customer_three'), t('login.preview_message_three')],
                ].map(([avatar, name, message], index) => (
                  <div key={name} className={`flex items-start gap-3 py-2 ${index > 0 ? 'border-t border-default/50' : ''}`}>
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-recessed text-[10px] font-semibold text-secondary">
                      {avatar}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-semibold text-primary">{name}</span>
                      <span className="mt-1 block truncate text-[11px] text-muted">{message}</span>
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col justify-between bg-canvas/40 p-4">
                <div>
                  <div className="max-w-[85%] rounded-2xl rounded-es-md bg-recessed px-3 py-2 text-xs leading-5 text-secondary">
                    {t('login.preview_inbound')}
                  </div>
                  <div className="ms-auto mt-3 max-w-[88%] rounded-2xl rounded-ee-md bg-primary-container px-3 py-2 text-xs leading-5 text-on-primary-container">
                    {t('login.preview_outbound')}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="rounded-xl border border-default/60 bg-surface px-3 py-2">
                    <span className="block text-base font-bold text-primary tabular-nums">3m</span>
                    <span className="text-[10px] text-muted">{t('login.preview_reply')}</span>
                  </div>
                  <div className="rounded-xl border border-default/60 bg-surface px-3 py-2">
                    <span className="block text-base font-bold text-primary tabular-nums">92%</span>
                    <span className="text-[10px] text-muted">{t('login.preview_sla')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="relative flex items-center gap-2 text-xs text-muted">
          <ShieldCheck size={15} className="text-primary" aria-hidden="true" />
          {t('login.security_note')}
        </p>
      </aside>

      <main className="flex min-h-dvh items-center justify-center px-5 py-20 sm:px-10 lg:h-dvh lg:min-h-0 lg:overflow-hidden lg:px-14 lg:py-10">
        <div className="w-full max-w-[430px]">
          <div className="mb-9 lg:hidden">
            <ProjectLockup />
          </div>

          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              {t('login.workspace_access')}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-[-0.035em] text-primary">
              {t('login.title')}
            </h2>
            <p className="mt-2 text-sm leading-6 text-secondary">
              {t('login.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label htmlFor="login-email" className="mb-2 block text-sm font-semibold text-primary">
                {t('login.email')}
              </label>
              <div className="relative">
                <Mail
                  size={17}
                  aria-hidden="true"
                  className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-muted"
                />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  dir="ltr"
                  className="h-12 w-full rounded-xl border border-default bg-surface ps-11 pe-4 text-sm
                    text-primary placeholder:text-muted shadow-sm transition-[border-color,box-shadow,background]
                    focus:border-primary/55 focus:bg-surface-hover focus:outline-none
                    focus:[box-shadow:0_0_0_3px_rgb(var(--md-sys-color-primary)/0.10)]"
                  placeholder="name@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="mb-2 block text-sm font-semibold text-primary">
                {t('login.password')}
              </label>
              <div className="relative">
                <LockKeyhole
                  size={17}
                  aria-hidden="true"
                  className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-muted"
                />
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-12 w-full rounded-xl border border-default bg-surface ps-11 pe-4 text-sm
                    text-primary shadow-sm transition-[border-color,box-shadow,background]
                    focus:border-primary/55 focus:bg-surface-hover focus:outline-none
                    focus:[box-shadow:0_0_0_3px_rgb(var(--md-sys-color-primary)/0.10)]"
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-remember" className="flex cursor-pointer items-start gap-3">
                <input
                  id="login-remember"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  aria-describedby="login-remember-hint"
                  className="mt-0.5 size-5 shrink-0 cursor-pointer rounded border-default accent-[rgb(var(--md-sys-color-primary))]"
                />
                <span className="text-sm font-semibold text-primary">{t('login.remember_label')}</span>
              </label>
              <p id="login-remember-hint" className="mt-1.5 ps-8 text-xs leading-5 text-muted">
                {t('login.remember_hint')}
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-xl border border-error/25 bg-tint-danger px-4 py-3 text-sm text-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-1 flex h-12 w-full items-center justify-center gap-2 rounded-xl
                bg-action-primary px-5 text-sm font-semibold text-action-primary shadow-md
                transition-[transform,box-shadow,opacity] duration-base ease-enter
                hover:-translate-y-px hover:shadow-lg active:translate-y-0 active:shadow-sm
                disabled:cursor-not-allowed disabled:opacity-45 motion-reduce:transform-none"
            >
              {loading ? t('login.submitting') : t('login.submit')}
              {!loading && <ArrowRight size={17} className="rtl:rotate-180" aria-hidden="true" />}
            </button>
          </form>

          <div className="mt-7 flex items-start gap-2 rounded-xl bg-recessed px-4 py-3 text-xs leading-5 text-secondary">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
            <span>{t('login.support_note')}</span>
          </div>

          <p className="mt-8 text-center text-xs text-muted">
            {t('login.footer')}
          </p>
        </div>
      </main>
    </div>
  );
}
